"use client";

import Script from "next/script";
import { usePathname } from "next/navigation";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AnalyticsTracker from "@/components/analytics/AnalyticsTracker";
import FeedbackBubble from "@/components/FeedbackBubble";
import { CLARITY_PROJECT_ID, GA_MEASUREMENT_ID } from "@/lib/site";

export default function SiteChrome({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const isAdmin = pathname?.startsWith("/admin") ?? false;
  const isStandalone = pathname?.startsWith("/resume-editor") || isAdmin;

  // Admin panel usage isn't real visitor traffic — tracking it would
  // pollute analytics with our own internal sessions. Scoped to /admin
  // specifically, not the whole isStandalone group (resume-editor usage is
  // real product usage worth tracking). GA/Clarity live here (not
  // app/layout.tsx, a server component with no route awareness) so the
  // same isAdmin check that already gates AnalyticsTracker/FeedbackBubble
  // can gate them too.
  const analytics = !isAdmin ? <AnalyticsTracker /> : null;
  const feedbackBubble = !isAdmin ? <FeedbackBubble /> : null;
  const thirdPartyAnalytics = !isAdmin ? (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
        strategy="afterInteractive"
      />
      <Script id="ga-init" strategy="afterInteractive">
        {`window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA_MEASUREMENT_ID}', { send_page_view: false });`}
      </Script>
      <Script id="ms-clarity" strategy="afterInteractive">
        {`(function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", "${CLARITY_PROJECT_ID}");`}
      </Script>
    </>
  ) : null;

  if (isStandalone) {
    return (
      <>
        {thirdPartyAnalytics}
        {analytics}
        <main className="flex-1">{children}</main>
        {feedbackBubble}
      </>
    );
  }

  return (
    <>
      {thirdPartyAnalytics}
      {analytics}
      <Header />
      <main className="flex-1 pt-20">{children}</main>
      <Footer />
      {feedbackBubble}
    </>
  );
}
